import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import PageForm from '../../templates/PageForm';
import ATable, { ATableHeader } from '../../organisms/ATable';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { IoIosRefresh } from 'react-icons/io';
import { FaFileCode, FaFileExcel, FaTimes } from 'react-icons/fa';
import { FaCheck } from 'react-icons/fa6';
import FieldsModal, { FieldItem } from './FieldsModal';
import NewTooltip from '../../atoms/NewTooltip';
import PresentationModal from '../../organisms/PresentationModal';
import CopyButton from '../../atoms/CopyButton';
import { JsonView, allExpanded, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import useFetch from '../../../hooks/useFetch';
import PageFooter from '../../molecules/PageFooter';
import TableSkeleton from '../../atoms/TableSkeleton';
import FormSkeleton from '../../atoms/FormSkeleton';
import Tab, { TabProps } from '../../molecules/Tab';
import CardList from '../../organisms/CardList';
import appConfig from '../../../config/app-config';
import { dataProviderApi, isTimeoutOrNetworkError } from '../../../services/dataProviderApi';
import PageSearch from '../../molecules/PageSearch';
import {
    BatchReportInfo,
    BatchReportItem,
    ReportItem,
    getEventColor,
    getCssColorBy,
    getContentByStatus,
    getStatusLabel,
    exportPageToCsv,
    isPendingCell,
    canRetryBatch,
} from './batchReportUtils';
import { NOT_COLLECTED_LABEL } from '../recommendation-engine/utils/partialCollection';
import ReportStatusBadge from './ReportStatusBadge';
import Dialog from '../../organisms/Dialog';
import { AuthContext } from '../../../services/Auth/AuthContext';
import { canInterruptBatchReport } from '../../../services/Auth/roles';

const DATA_PROVIDER_ENV = appConfig.apis.dataProviderApiEnv;
const API_KEY = DATA_PROVIDER_ENV.key;
const BASE_URL = `${DATA_PROVIDER_ENV.address}/batch-reports`;

// --- Sub-components ---

const ExportDropdown: React.FC<{ onExportAll: () => void; onExportPage: () => void; isLoading: boolean; disablePageExport?: boolean }> = ({ onExportAll, onExportPage, isLoading, disablePageExport }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) setIsOpen(false);
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    return (
        <div ref={ref} className="relative inline-block text-left ml-2">
            <button
                type="button"
                className="text-white bg-blue-500 hover:bg-blue-700 font-medium rounded text-sm px-4 py-2 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setIsOpen(!isOpen)}
                disabled={isLoading}
            >
                <FaFileExcel className="mr-2" />
                {isLoading ? 'Exportando...' : 'Exportar'}
                <svg className="ml-2 w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-52 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                        <button onClick={() => { setIsOpen(false); onExportAll(); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                            <FaFileExcel className="mr-2 text-green-600" /> Lote completo
                        </button>
                        <button onClick={() => { setIsOpen(false); onExportPage(); }} disabled={disablePageExport} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                            <FaFileCode className="mr-2 text-blue-600" /> Página atual
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const InfoCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm h-full">
        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{title}</h3>
        {children}
    </div>
);

const CountBadge: React.FC<{ count: number; label: string; colorClass: string; onClick?: () => void }> = ({ count, label, colorClass, onClick }) => (
    <div className="flex items-center gap-2 cursor-pointer hover:opacity-80" onClick={onClick}>
        <span className={`${colorClass} text-white text-xs font-bold px-2 py-0.5 rounded`}>{count}</span>
        <span className="text-sm text-gray-600">{label}</span>
    </div>
);

// Só é possível interromper enquanto o lote ainda está em andamento.
const canShowInterrupt = (status?: string): boolean => status === 'PROCESSING' || status === 'PENDING';

// --- Main Component ---

const BatchReportPageView: React.FC = () => {
    const { id = '', page = '0', pageSize = '100', field = 'document', order = 'ASC' } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useContext(AuthContext);

    const currentTab = searchParams.get('tab') || 'extraction';
    const statusFilter = searchParams.get('status') || '';

    const apiVersion = useSignal('');
    const headers = useSignal<ATableHeader[]>([]);
    const rows = useSignal<any[]>([]);
    const showValueModal = useSignal(false);
    const selectedValue = useSignal<any | null>(null);
    const report = useSignal<BatchReportInfo | null>(null);
    const showFieldsModal = useSignal(false);
    const selectedFields = useSignal<FieldItem[]>([]);
    const loadingFields = useSignal(false);
    const totalPages = useSignal(0);
    const totalElements = useSignal(0);
    const refreshKey = useSignal(0);
    const showInterruptDialog = useSignal(false);
    const [interrupting, setInterrupting] = useState(false);

    // Reports tab
    const reports = useSignal<ReportItem[]>([]);
    const reportsTotalPages = useSignal(0);
    const reportsTotalElements = useSignal(0);
    const [documentSearch, setDocumentSearch] = useState('');
    const [selectedStatus, setSelectedStatus] = useState(statusFilter);
    const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
    const [reprocessing, setReprocessing] = useState(false);

    const currentPage = parseInt(page);
    const currentPageSize = parseInt(pageSize);
    const currentField = field;
    const currentOrder = order as 'ASC' | 'DESC';

    const handleBack = () => {
        if (location.state?.fromForm) {
            navigate('/data-provider/batch-report/0/100/createdAt/DESC');
        } else {
            navigate(-1);
        }
    };

    const exportLoading = useSignal(false);

    const handleExport = async () => {
        exportLoading.value = true;
        try {
            await dataProviderApi.downloadBatchCsv(id);
        } catch (e) {
            alert('Erro ao exportar relatório completo. Tente novamente.');
        } finally {
            exportLoading.value = false;
        }
    };

    const handleExportPage = () => {
        exportPageToCsv(rows.value, headers.value, id, currentPage);
    };

    const pageChanged = (p: number) => {
        navigate(`/data-provider/batch-report/view/${id}/${p}/${currentPageSize}/${currentField}/${currentOrder}`, { replace: true });
    };

    const pageSizeChanged = (size: number) => {
        navigate(`/data-provider/batch-report/view/${id}/0/${size}/${currentField}/${currentOrder}`, { replace: true });
    };

    const pageSorted = (sortField: string, sortOrder: 'ASC' | 'DESC') => {
        navigate(`/data-provider/batch-report/view/${id}/${currentPage}/${currentPageSize}/${sortField}/${sortOrder}`, { replace: true });
    };

    const handleTabChange = (tabName: string) => {
        setSearchParams({ tab: tabName });
    };

    const handleStatusClick = (status: string) => {
        setSelectedStatus(status);
        setSearchParams({ tab: 'reports', status });
    };

    const handleReportClick = (documentRaw: string) => {
        navigate(`/data-provider/batch-report/view/${id}/report/${encodeURIComponent(documentRaw)}`);
    };

    const toggleDocumentSelection = (documentRaw: string, e: React.MouseEvent) => {
        setSelectedDocuments(prev =>
            prev.includes(documentRaw) ? prev.filter(d => d !== documentRaw) : [...prev, documentRaw]
        );
    };

    const handleReprocess = async () => {
        if (!selectedDocuments.length) return;
        setReprocessing(true);
        try {
            const result = await dataProviderApi.reprocessReports(id, selectedDocuments);
            // Update local report statuses to PROCESSING for reprocessed documents
            const reprocessed = result.documentsReprocessed || selectedDocuments;
            reports.value = reports.value.map(r => {
                const raw = r.documentRaw || r.document;
                if (reprocessed.includes(raw)) {
                    return { ...r, status: 'PROCESSING', statusFormatted: 'Processando', canManualRetry: false };
                }
                return r;
            });
            setSelectedDocuments([]);
        } catch {
            alert('Erro ao reprocessar relatórios');
        } finally {
            setReprocessing(false);
        }
    };

    const renderStatusTooltip = (status?: string, errorMessage?: string | null) => {
        return getContentByStatus(status, errorMessage);
    };

    // Fetch batch report data
    const pageViewParams = new URLSearchParams({ page: String(currentPage), pageSize: String(currentPageSize), order: `${currentField},${currentOrder}`, _t: String(refreshKey.value) });
    if (documentSearch) pageViewParams.set('document', documentSearch);
    if (selectedStatus) pageViewParams.set('status', selectedStatus);

    const { isLoading, data, error, isTokenError } = useFetch(
        `${BASE_URL}/${id}/page-view?${pageViewParams.toString()}`,
        [{ key: 'x-api-key', value: API_KEY }]
    );

    // Fetch reports with filters
    const reportsQueryParams = new URLSearchParams({ page: String(currentPage), pageSize: String(currentPageSize) });
    if (documentSearch) reportsQueryParams.set('document', documentSearch);
    if (selectedStatus) reportsQueryParams.set('status', selectedStatus);

    const reportsUrl = currentTab === 'reports' ? `${BASE_URL}/${id}/reports?${reportsQueryParams.toString()}` : '';
    const { isLoading: isLoadingReports, data: reportsData } = useFetch(reportsUrl, [{ key: 'x-api-key', value: API_KEY }]);

    // Fetch batch info (for provider tab)
    const batchInfoUrl = currentTab === 'provider' ? `${BASE_URL}/${id}` : '';
    const { data: batchInfoData } = useFetch(batchInfoUrl, [{ key: 'x-api-key', value: API_KEY }]);

    if (isTokenError) navigate('/denied');
    if (error) navigate('/network-error');

    useEffect(() => {
        if (data) {
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;
            totalPages.value = data.totalPages || 0;
            totalElements.value = data.totalElements || 0;

            const rpt = data.batch || data;
            if (rpt) {
                report.value = {
                    id: rpt.id,
                    provider: rpt.provider,
                    createdAt: rpt.createdAt,
                    updatedAt: rpt.updatedAt,
                    status: rpt.status,
                    documents: rpt.documentsCount || 0,
                    errors: rpt.errorCount || 0,
                    success: rpt.successCount || 0,
                    sending: rpt.sendingCount || 0,
                    waiting: rpt.waitingCount || 0,
                    pending: rpt.pendingCount || 0,
                    interrupted: rpt.interruptedCount || 0,
                    statusFormatted: rpt.statusFormatted,
                    reportType: rpt.reportType,
                    batchReportId: rpt.batchReportId,
                    canUpdateStatus: rpt.canUpdateStatus,
                    mini_uuid: rpt.mini_uuid,
                    identification: rpt.identification,
                    reportTypeInfo: rpt.reportTypeInfo,
                };
            }

            const dynamicHeaders: ATableHeader[] = (data.fields || []).map((h: any, index: number) => ({
                id: h.field || `column-${index}`,
                title: h.header || `column-${index}`,
                field: h.field || `column-${index}`,
                canSort: false,
                cssClass: 'text-center',
            }));

            headers.value = [
                { id: 'document', title: 'Documento', field: 'document', canSort: true, cssClass: 'text-center', width: '150px', fixed: 'left' },
                { id: 'createdAt', title: 'Consulta em', canSort: false, cssClass: 'text-center', width: '140px' },
                ...dynamicHeaders,
                { id: 'status', title: 'Status', field: 'status', canSort: false, cssClass: 'text-center', width: '100px', fixed: 'right' },
            ];

            rows.value = data.list || data.items || data.reports || [];
        }
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (reportsData) {
            reports.value = reportsData.reports || [];
            reportsTotalPages.value = reportsData.totalPages || 0;
            reportsTotalElements.value = reportsData.totalElements || 0;
        }
    }, [reportsData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (statusFilter) setSelectedStatus(statusFilter);
    }, [statusFilter]);

    const tabItems: TabProps[] = [
        { id: 'extraction', title: 'Extração', index: 0 },
        { id: 'reports', title: 'Relatórios', index: 1 },
        { id: 'provider', title: 'Provedor', index: 2 },
    ];

    const forceReload = () => { refreshKey.value = refreshKey.value + 1; };

    const handleInterrupt = async () => {
        if (!id) return;
        setInterrupting(true);
        try {
            await dataProviderApi.interruptBatch(id);
            toast.success('Processamento interrompido.');
            showInterruptDialog.value = false;
            forceReload();
        } catch (error) {
            if (isTimeoutOrNetworkError(error)) {
                toast.warning('A interrupção demorou mais que o esperado e pode estar parcial. Clique em Interromper novamente para concluir.');
                showInterruptDialog.value = false;
                forceReload();
            } else {
                toast.error('Erro ao interromper o processamento. Tente novamente.');
                forceReload();
            }
        } finally {
            setInterrupting(false);
        }
    };

    const renderValue = (value: any) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'boolean') {
            return value ? <FaCheck className="mx-auto text-green-600 text-xl" /> : <FaTimes className="mx-auto text-red-600 text-xl" />;
        }
        if (Array.isArray(value)) {
            const isPrimitive = value.every((v) => ['string', 'number', 'boolean'].includes(typeof v));
            if (isPrimitive) return value.join(', ');
            return (
                <FaFileCode className="mx-auto text-blue-600 cursor-pointer text-xl" onClick={() => { selectedValue.value = value; showValueModal.value = true; }} />
            );
        }
        if (typeof value === 'object') {
            return (
                <FaFileCode className="mx-auto text-blue-600 cursor-pointer text-xl" onClick={() => { selectedValue.value = value; showValueModal.value = true; }} />
            );
        }
        return value;
    };

    const renderRows = (items: BatchReportItem[]) => {
        if (!items || items.length === 0) {
            return (
                <tr><td colSpan={headers.value.length} className="py-2 px-2 text-center">
                    <div className="text-2xl font-bold text-gray-500 mt-4">Nenhum registro encontrado...</div>
                </td></tr>
            );
        }
        return items.map((row, index) => (
            <tr key={index} className={`${index % 2 === 0 ? 'bg-slate-100' : ''} border-b border-gray-300 hover:bg-slate-300 hover:shadow-md`}>
                {headers.value.map((h) => (
                    <td key={h.id} className="py-2 px-2 text-center">
                        {h.id === 'status' ? (
                            <NewTooltip content={renderStatusTooltip(row.status, row.errorMessage)} alignment="left">
                                <span className={`rounded px-1 py-1 text-xs font-bold text-white ${row.status === 'WAITING' ? 'bg-orange-500' : getCssColorBy(row.status)}`}>
                                    {row.status === 'WAITING' ? 'Retentar' : getStatusLabel(row.status)}
                                </span>
                            </NewTooltip>
                        ) : isPendingCell(row, h.field || h.id) ? (
                            <span className="text-xs font-medium text-amber-700 dark:text-amber-300">{NOT_COLLECTED_LABEL}</span>
                        ) : renderValue((row as any)[h.field || h.id])}
                    </td>
                ))}
            </tr>
        ));
    };

    const renderReportCard = (item: ReportItem) => (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleReportClick(item.documentRaw || item.document)}>
            <div className="space-y-2">
                <div className="flex justify-between items-start">
                    <p className="font-semibold text-gray-900">{item.document}</p>
                    {item.canManualRetry && canRetryBatch(report.value?.status) ? (
                        <NewTooltip content="Selecione para reprocessar este relatório" alignment="left">
                            <label className="flex items-center gap-1.5 bg-orange-50 border border-orange-300 rounded-full px-2.5 py-1 cursor-pointer hover:bg-orange-100 transition-colors" onClick={(e) => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    checked={selectedDocuments.includes(item.documentRaw || item.document)}
                                    onChange={() => toggleDocumentSelection(item.documentRaw || item.document, {} as React.MouseEvent)}
                                    className="w-3.5 h-3.5 accent-orange-500 cursor-pointer"
                                />
                                <span className="text-xs font-semibold text-orange-600">Retentar</span>
                            </label>
                        </NewTooltip>
                    ) : (
                        <ReportStatusBadge status={item.status} />
                    )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><p className="text-gray-500">Criado em</p><p className="text-gray-900">{item.createdAt || '-'}</p></div>
                    <div><p className="text-gray-500">Atualizado em</p><p className="text-gray-900">{item.updatedAt || '-'}</p></div>
                    <div><p className="text-gray-500">Tentativas</p><p className="text-gray-900">{item.attempts || 0} / {item.maxAttempts || 3}</p></div>
                </div>
                {item.events && item.events.length > 0 && (
                    <div className="flex gap-1 mt-2">
                        {item.events.map((event, idx) => (
                            <NewTooltip key={idx} content={<div className="text-xs"><div className="font-semibold">{event.eventName}</div><div>{event.occurredAt}</div></div>} alignment="right">
                                <div className={`w-3 h-3 rounded-sm ${getEventColor(event.eventName)} cursor-pointer`} />
                            </NewTooltip>
                        ))}
                    </div>
                )}
                {item.errorMessage && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                        <p className="text-xs text-red-600">{item.errorMessage}</p>
                    </div>
                )}

            </div>
        </div>
    );

    return (
        <PageForm
            pageTitle="Relatório em lote"
            apiVersion={apiVersion.value}
            isLoading={isLoading || loadingFields.value}
            breadcrumb={[
                { label: 'Relatórios em lote', url: '/data-provider/batch-report/0/100/createdAt/DESC' },
                { label: 'Detalhe do relatório em lote', url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end">
                    <button className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded" onClick={handleBack}>
                        <IoMdArrowRoundBack />
                    </button>
                    {report.value?.status !== 'COMPLETED' && (
                        <button onClick={forceReload} className="p-2 ml-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors" title="Atualizar dados">
                            <IoIosRefresh size={18} />
                        </button>
                    )}
                    {canInterruptBatchReport(user) && canShowInterrupt(report.value?.status) && (
                        <button
                            onClick={() => { showInterruptDialog.value = true; }}
                            className="text-white bg-red-600 hover:bg-red-700 font-medium rounded text-sm px-4 py-2 ml-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Interromper processamento
                        </button>
                    )}
                    {selectedDocuments.length > 0 && canRetryBatch(report.value?.status) && (
                        <button
                            onClick={handleReprocess}
                            disabled={reprocessing}
                            className="text-white bg-orange-500 hover:bg-orange-600 font-medium rounded text-sm px-4 py-2 ml-2 disabled:opacity-50"
                        >
                            {reprocessing ? 'Reprocessando...' : `Retentar (${selectedDocuments.length})`}
                        </button>
                    )}
                    <ExportDropdown onExportAll={handleExport} onExportPage={handleExportPage} isLoading={exportLoading.value} disablePageExport={currentTab !== 'extraction'} />
                </div>
            }
        >
            <div className="p-2 space-y-2">
                {isLoading ? <FormSkeleton /> : report.value && (
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-7">
                            <InfoCard title="Dados do Relatório">
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-6">
                                        <span><span className="text-gray-500"># </span><span className="font-medium">{report.value.id}</span></span>
                                        <span className="flex-1"><span className="text-gray-500">Identificação: </span><span className="font-medium">{report.value.identification}</span></span>
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <span><span className="text-gray-500">Provedor: </span><span className="font-medium">{report.value.provider}</span></span>
                                        <span><span className="text-gray-500">Tipo: </span><span className="font-medium">{report.value.reportType}</span></span>
                                    </div>
                                    <div className="flex items-center gap-6 pt-2 mt-2 border-t border-gray-100">
                                        <span className="inline-flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                                            <span className="text-gray-500">Criado em:</span>
                                            <span className="font-medium">{report.value.createdAt}</span>
                                        </span>
                                        {report.value.updatedAt && (
                                            <span className="inline-flex items-center gap-1.5">
                                                <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                                                <span className="text-gray-500">Atualizado em:</span>
                                                <span className="font-medium">{report.value.updatedAt}</span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </InfoCard>
                        </div>
                        <div className="col-span-5">
                            <InfoCard title="Totais e Status">
                                <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-100">
                                    <ReportStatusBadge status={report.value.status} />
                                    <span className="text-sm text-gray-600 cursor-pointer hover:underline" onClick={() => handleStatusClick('')}>Todos os documentos: <strong>{report.value.documents || 0}</strong></span>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <CountBadge count={report.value.sending || 0} label="Processando" colorClass="bg-gray-500" onClick={() => handleStatusClick('PROCESSING')} />
                                    <CountBadge count={report.value.pending || 0} label="Registrados" colorClass="bg-yellow-500" onClick={() => handleStatusClick('PENDING')} />
                                    <CountBadge count={report.value.waiting || 0} label="Aguardando" colorClass="bg-orange-400" onClick={() => handleStatusClick('WAITING')} />
                                    <CountBadge count={report.value.success || 0} label="Concluídos" colorClass="bg-blue-500" onClick={() => handleStatusClick('COMPLETED')} />
                                    <CountBadge count={report.value.errors || 0} label="Erros" colorClass="bg-red-500" onClick={() => handleStatusClick('ERROR')} />
                                    <CountBadge count={0} label="Falhou" colorClass="bg-red-800" onClick={() => handleStatusClick('FAILED')} />
                                    <CountBadge count={report.value.interrupted || 0} label="Interrompidos" colorClass="bg-slate-500" onClick={() => handleStatusClick('INTERRUPTED')} />
                                </div>
                            </InfoCard>
                        </div>
                    </div>
                )}

                <div className="mt-4 flex items-center gap-4">
                    <Tab tabItems={tabItems} tabIndex={tabItems.findIndex(t => t.id === currentTab)} setTabIndex={(index) => handleTabChange(tabItems[index].id)} />
                    <div className="flex gap-2 ml-auto h-[46px]">
                        <PageSearch
                            value={documentSearch}
                            label="Buscar por documento..."
                            onSearch={(val) => setDocumentSearch(val)}
                            filterCleanVisible={true}
                            filterCleanEnabled={Boolean(documentSearch)}
                            onFilterClean={() => setDocumentSearch('')}
                            configureVisible={false}
                            className="w-72"
                        />
                    </div>
                </div>

                {/* Extraction Tab */}
                {currentTab === 'extraction' && (
                    <>
                        {isLoading ? <TableSkeleton rows={currentPageSize > 10 ? 10 : currentPageSize} /> : (
                            <ATable headers={headers.value} fieldSorted={currentField} orderSorted={currentOrder} onSort={pageSorted} autoResize={true} marginTop={385} enableFixedColumns={true}>
                                {renderRows(rows.value)}
                            </ATable>
                        )}
                        {!isLoading && (
                            <PageFooter currentPage={currentPage} totalPages={totalPages.value} itemsPerPage={currentPageSize} totalElements={totalElements.value} onPageChange={pageChanged} onItemsPerPageChange={pageSizeChanged} itemsLabel="registros" />
                        )}
                    </>
                )}

                {/* Reports Tab */}
                {currentTab === 'reports' && (
                    <>
                        <div className="overflow-y-auto border-b border-gray-200" style={{ height: 'calc(100vh - 385px)' }}>
                            {isLoadingReports ? <TableSkeleton rows={6} /> : (
                                <CardList items={reports.value} renderCard={renderReportCard} columns={3} emptyMessage="Nenhum relatório encontrado" />
                            )}
                        </div>
                        {!isLoadingReports && reports.value.length > 0 && (
                            <PageFooter currentPage={currentPage} totalPages={reportsTotalPages.value} itemsPerPage={currentPageSize} totalElements={reportsTotalElements.value} onPageChange={pageChanged} onItemsPerPageChange={pageSizeChanged} itemsLabel="relatórios" />
                        )}
                    </>
                )}

                {/* Provider Tab */}
                {currentTab === 'provider' && (
                    <div className="space-y-4 mt-4">
                        <InfoCard title="Configuração do Provedor">
                            <div className="grid grid-cols-3 gap-4 text-sm">
                                <div><span className="text-gray-500">Provedor:</span> <span className="font-medium">{report.value?.provider || '-'}</span></div>
                                <div><span className="text-gray-500">Tipo de documento:</span> <span className="font-medium">{batchInfoData?.reportTypeInfo?.documentType || '-'}</span></div>
                                <div><span className="text-gray-500">Report Type:</span> <span className="font-medium">{batchInfoData?.reportTypeInfo?.name || '-'}</span></div>
                                {batchInfoData?.reportTypeInfo?.description && (
                                    <div className="col-span-3"><span className="text-gray-500">Descrição:</span> <span className="font-medium">{batchInfoData.reportTypeInfo.description}</span></div>
                                )}
                            </div>
                        </InfoCard>
                        {batchInfoData?.fields && batchInfoData.fields.length > 0 && (
                            <InfoCard title="Campos de Extração">
                                <div className="flex flex-wrap gap-1">
                                    {batchInfoData.fields.map((f: any, idx: number) => (
                                        <span key={idx} className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded border border-blue-400">
                                            {f.header || f.field || '-'}
                                        </span>
                                    ))}
                                </div>
                            </InfoCard>
                        )}
                        {batchInfoData?.events && batchInfoData.events.length > 0 && (
                            <InfoCard title="Histórico de Eventos">
                                <div className="overflow-y-auto max-h-80">
                                    <div className="relative border-l-2 border-gray-200 ml-3">
                                        {batchInfoData.events.map((event: any, idx: number) => (
                                            <div key={idx} className="mb-4 ml-4 relative">
                                                <div className={`absolute -left-[21px] top-1.5 w-3 h-3 rounded-full ${getEventColor(event.eventName)}`} />
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">{event.eventName}</p>
                                                        {event.payload && Object.keys(event.payload).length > 0 && (
                                                            <div className="mt-1 flex flex-wrap gap-1">
                                                                {Object.entries(event.payload).filter(([k]) => k !== 'batch_report_id').map(([k, v]) => (
                                                                    <span key={k} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                                                        {k}: {Array.isArray(v) ? `[${v.length} itens]` : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <span className="text-xs text-gray-400 whitespace-nowrap ml-4">{event.occurredAt}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </InfoCard>
                        )}
                        {/* Extractor Mapping + Processing History side by side */}
                        <div className={`grid gap-4 ${batchInfoData?.extractorMapping && Object.keys(batchInfoData.extractorMapping).length > 0 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                            {batchInfoData?.extractorMapping && Object.keys(batchInfoData.extractorMapping).length > 0 && (
                                <InfoCard title="Mapeamento do Extrator">
                                    <div className="overflow-x-auto max-h-60 overflow-y-auto">
                                        <table className="w-full text-sm">
                                            <thead><tr className="border-b"><th className="text-left py-1 px-2 text-gray-500">Chave</th><th className="text-left py-1 px-2 text-gray-500">Valor</th></tr></thead>
                                            <tbody>
                                                {Object.entries(batchInfoData.extractorMapping).map(([key, val]) => (
                                                    <tr key={key} className="border-b border-gray-100">
                                                        <td className="py-1 px-2 font-medium">{key}</td>
                                                        <td className="py-1 px-2">{String(val)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </InfoCard>
                            )}
                            <InfoCard title="Processamentos Realizados">
                                <div className="overflow-x-auto max-h-60 overflow-y-auto">
                                    {batchInfoData?.fetches && batchInfoData.fetches.length > 0 ? (
                                        <table className="w-full text-sm">
                                            <thead><tr className="border-b"><th className="text-left py-1 px-2 text-gray-500">#</th><th className="text-left py-1 px-2 text-gray-500">Status</th><th className="text-left py-1 px-2 text-gray-500">Docs</th><th className="text-left py-1 px-2 text-gray-500">Limite</th><th className="text-left py-1 px-2 text-gray-500">Início</th><th className="text-left py-1 px-2 text-gray-500">Fim</th></tr></thead>
                                            <tbody>
                                                {batchInfoData.fetches.map((f: any, idx: number) => (
                                                    <tr key={idx} className="border-b border-gray-100">
                                                        <td className="py-1 px-2 font-medium">{f.attempt}</td>
                                                        <td className="py-1 px-2">
                                                            <span className={`rounded px-2 py-0.5 text-xs font-bold text-white ${f.status === 'SUCCEEDED' ? 'bg-green-500' : f.status === 'RUNNING' ? 'bg-blue-500' : 'bg-red-500'}`}>
                                                                {f.statusFormatted || f.status}
                                                            </span>
                                                        </td>
                                                        <td className="py-1 px-2">{f.documentsCount}</td>
                                                        <td className="py-1 px-2">{f.params?.limit || '-'}</td>
                                                        <td className="py-1 px-2 text-xs">{f.startedAt?.substring(11, 19) || '-'}</td>
                                                        <td className="py-1 px-2 text-xs">{f.completedAt?.substring(11, 19) || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <p className="text-sm text-gray-400 p-2">Nenhum processamento registrado</p>
                                    )}
                                </div>
                            </InfoCard>
                        </div>
                    </div>
                )}

                {/* Modals */}
                <PresentationModal id="value-modal" show={showValueModal.value} onClose={() => { showValueModal.value = false; selectedValue.value = null; }} header="Detalhes" footer={<CopyButton data={selectedValue.value} />}>
                    <Fragment>
                        <div className="p-4">
                            <div style={{ height: '500px', overflowY: 'auto' }}>
                                {selectedValue.value && <JsonView data={selectedValue.value} shouldExpandNode={allExpanded} style={defaultStyles} />}
                            </div>
                        </div>
                    </Fragment>
                </PresentationModal>
                <FieldsModal id="fields-modal" show={showFieldsModal.value} fields={selectedFields.value} onClose={() => { showFieldsModal.value = false; selectedFields.value = []; }} />
                <Dialog
                    id="interrupt-batch-dialog"
                    show={showInterruptDialog.value}
                    onClose={() => { showInterruptDialog.value = false; }}
                    header="Interromper processamento"
                    size="sm"
                    body={
                        <div className="p-6 text-sm text-gray-700 dark:text-gray-200 space-y-2">
                            <p>
                                Apenas os itens em andamento (pendentes, processando e aguardando) serão
                                interrompidos.
                            </p>
                            <p>
                                Os itens já concluídos e os que receberam erro são preservados e permanecem
                                como estão. Esta ação não pode ser desfeita.
                            </p>
                        </div>
                    }
                    footer={
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                className="text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 font-medium rounded text-sm px-4 py-2"
                                onClick={() => { showInterruptDialog.value = false; }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="text-white bg-red-600 hover:bg-red-700 font-medium rounded text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleInterrupt}
                                disabled={interrupting}
                            >
                                {interrupting ? 'Interrompendo...' : 'Interromper'}
                            </button>
                        </div>
                    }
                />
            </div>
        </PageForm>
    );
};

export default BatchReportPageView;
